import { useSelector } from "src/store";
import { useLicenses } from "src/ui/containers/dashboard/licenses/api/helpers";

import { defineAbility, MongoAbility } from "@casl/ability";
import { Device, Group, GroupMember, License } from "@dashboard/devices/types";
import { FileInfo } from "@dashboard/files/types";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Workspace } from "@dashboard/workspaces/types";

import AuthService from "../authService";

type GroupAbilities = ["create" | "delete" | "update", "Group"];
type LicenseAbilities = ["manage" | "create", "License"];
type WorkspaceAbilities = ["update" | "cancel", "Workspace"];
type FileAbilities = ["manage", "File"];
type AppAbilities = ["install" | "uninstall", "App"];
type TagAbilities = ["manage", "Tag"];
type MemberAbilities = ["manage", "Member"];
type OwnerAbilities = ["manage", "Owner"];
type UniversalLicenseAbilities = ["manage", "UniversalLicense"];
type ProductAbilities = ["manage", "Product"];

type Abilities =
    | GroupAbilities
    | LicenseAbilities
    | WorkspaceAbilities
    | FileAbilities
    | AppAbilities
    | TagAbilities
    | MemberAbilities
    | OwnerAbilities
    | UniversalLicenseAbilities
    | ProductAbilities;

const getGroupRole = (email: string | undefined, group: Group | null, groups: Group[]): GroupMember["role"] | null => {
    if (!email || !group) {
        return null;
    }

    const currentGroupRole = group.members.find((member) => member.email === email)?.role || null;

    if (!group.parentId) {
        return currentGroupRole;
    }

    const parentGroup = groups.find((g) => g.id === group.parentId);
    if (!parentGroup) {
        return currentGroupRole;
    }

    const parentGroupRole = getGroupRole(email, parentGroup, groups);

    if (currentGroupRole === "Owner" || parentGroupRole === "Owner") {
        return "Owner";
    }
    if (currentGroupRole === "Manager" || parentGroupRole === "Manager") {
        return "Manager";
    }
    if (currentGroupRole === "User" || parentGroupRole === "User") {
        return "User";
    }

    return null;
};

const getEffectiveRole = (group: Group | null, workspace: Workspace | null, email: string | undefined, groups: Group[]) => {
    const workspaceRole = workspace?.members.find((member) => member.email === email)?.role || null;
    const groupRole = getGroupRole(email, group, groups);

    if (groupRole === "Owner" || workspaceRole === "Owner") {
        return "Owner";
    }
    if (groupRole === "Manager" || workspaceRole === "Manager") {
        return "Manager";
    }
    if (groupRole === "User" || workspaceRole === "User") {
        return "User";
    }
    return null;
};

export const PermissionService = (email?: string) => {
    const groups = useSelector(selectGroups);
    const subscriptionExpired = useCurrentSubscriptionExpired();

    const { data: currentWorkspace } = useCurrentWorkspace();

    const emailAdrress = email || AuthService.getCurrentUser()?.email || "test@emteria.com";

    return {
        workspaceAbility: (workspace: Workspace | null) => {
            const role = getEffectiveRole(null, workspace, emailAdrress, groups);

            return defineAbility<MongoAbility<Abilities>>((can, _cannot) => {
                if (subscriptionExpired) {
                    if (role === "Owner") {
                        can(["cancel"], "Workspace");
                    }
                    if (role === "Owner" || role === "Manager") {
                        can(["manage"], "Member");
                    }
                } else {
                    if (role === "Owner") {
                        can(["cancel"], "Workspace");
                    }
                    if (role === "Owner" || role === "Manager") {
                        can(["update"], "Workspace");
                        can(["manage"], "Member");
                    }
                    if (role === "Owner" || role === "Manager" || role === "User") {
                        can(["manage"], "File");
                        can(["create"], "License");
                    }
                }

                if (role === "Owner") {
                    can(["manage"], "Owner");
                }
            });
        },
        groupAbility: (group: Group | null, workspace: Workspace | null) => {
            const role = getEffectiveRole(group, workspace, emailAdrress, groups);

            return defineAbility<MongoAbility<Abilities>>((can, _cannot) => {
                if (subscriptionExpired) {
                    if (role === "Owner" || role === "Manager") {
                        can(["manage"], "Member");
                    }
                } else if (role === "Owner" || role === "Manager") {
                    can(["create", "delete", "update"], "Group");
                    can(["manage"], "Member");
                    can(["manage"], "UniversalLicense");
                    can(["install", "uninstall"], "App");
                }
                if (role === "Owner" || role === "Manager" || role === "User") {
                    can(["manage"], "File");
                }

                if (role === "Owner") {
                    can(["manage"], "Owner");
                }
            });
        },
        licenseAbility: (license: License | null, workspace?: Workspace) => {
            const group = groups.find((item) => item.id === license?.groupId);
            const role = getEffectiveRole(group || null, workspace || currentWorkspace, emailAdrress, groups);

            return defineAbility<MongoAbility<Abilities>>((can, _cannot) => {
                if (subscriptionExpired) {
                    return;
                }
                if (role === "Owner") {
                    can(["manage"], "License");
                }
            });
        },
        fileAbility: (file: FileInfo) => {
            const group = groups.find((item) => item.id === file.groupId);
            const role = getEffectiveRole(group || null, currentWorkspace || null, emailAdrress, groups);

            return defineAbility<MongoAbility<Abilities>>((can, _cannot) => {
                if (subscriptionExpired) {
                    return;
                }
                if (role === "Owner" || role === "Manager") {
                    can(["manage"], "Tag");
                }
            });
        },
        deviceAbility: (device: Device) => {
            const licenses = useLicenses();
            const license = licenses.find((item) => item?.device?.id === device.id);
            const group = groups.find((item) => item.id === license?.groupId);
            const role = getEffectiveRole(group || null, currentWorkspace || null, emailAdrress, groups);

            return defineAbility<MongoAbility<Abilities>>((can, _cannot) => {
                if (subscriptionExpired) {
                    return;
                }
                if (role === "Owner" || role === "Manager") {
                    can(["install", "uninstall"], "App");
                }
            });
        },
        productAbility: (workspace: Workspace | null) => {
            const role = getEffectiveRole(null, workspace, emailAdrress, groups);

            return defineAbility<MongoAbility<Abilities>>((can, _cannot) => {
                if (subscriptionExpired) {
                    return;
                }
                if (role === "Owner" || role === "Manager") {
                    can(["manage"], "Product");
                }
            });
        },
    };
};
